import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  Directive,
  input,
  output,
} from '@angular/core';
import { Icon, type IconInput } from '../icon/icon';

/** Figma's `Type` property on `Feedback Banner`. */
export type FeedbackBannerType = 'success' | 'error' | 'warning' | 'info';

/** Figma's `Direction` property on `CTA Banner`. */
export type CtaBannerDirection = 'horizontal' | 'vertical';

/** The glyph each type defaults to. Figma's own choices, including the slash. */
const TYPE_ICONS: Record<FeedbackBannerType, IconInput> = {
  success: 'check-circle',
  error: 'slash',
  warning: 'alert-triangle',
  info: 'info',
};

/**
 * Feedback Banner — Figma's `Feedback Banner` (node 74:4142),
 * `Type`(Success/Error/Warning/Info) over boolean `title`, `description`,
 * `action` and `x` slots.
 *
 * ```html
 * <ui-feedback-banner type="success" heading="Saved" description="Your changes are live." />
 *
 * <ui-feedback-banner type="error" heading="Upload failed" dismissible (dismiss)="hide()">
 *   <button ui-button uiFeedbackBannerAction variant="secondary" size="sm">Retry</button>
 * </ui-feedback-banner>
 * ```
 *
 * **It announces itself.** A banner that appears after an action is useless to a
 * screen-reader user if nothing says so, and Figma specifies no role at all — an
 * error takes `role="alert"`, everything else `role="status"`, and `announce="false"`
 * turns both off for a banner that is simply part of the page on load.
 *
 * Figma fixes the width at 344px. Left fluid here: a feedback banner sits in
 * whatever column it is given, and 344 is the drawing, not a constraint.
 *
 * ⚠️ **`Type=Info` has no tokens.** Its fill and text bind two raw variables named
 * `100` and `700` (#e4f3fd / #0092ed) rather than anything in the published Color
 * collections, which have no `info` family at all. They are written here as
 * literals in both themes, so Info is the one type that does not adapt to dark —
 * deliberately, because inventing a plausible dark blue would hide the gap rather
 * than fix it. The pair is also 2.93:1. See docs/design-system-feedback.md #93.
 */
@Component({
  selector: 'ui-feedback-banner',
  imports: [Icon],
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './feedback-banner.scss',
  host: {
    '[class]': 'hostClasses()',
    '[attr.role]': 'role()',
  },
  template: `
    <span class="ui-feedback-banner__icon">
      <ui-icon [name]="icon() ?? TYPE_ICONS[type()]" [strokeWidth]="1.5" />
    </span>

    <span class="ui-feedback-banner__text">
      @if (heading()) {
        <span class="ui-feedback-banner__heading">{{ heading() }}</span>
      }
      @if (description()) {
        <span class="ui-feedback-banner__description">{{ description() }}</span>
      }
    </span>

    <ng-content select="[uiFeedbackBannerAction]" />

    @if (dismissible()) {
      <button
        class="ui-feedback-banner__dismiss"
        type="button"
        [attr.aria-label]="dismissLabel()"
        (click)="dismiss.emit()"
      >
        <ui-icon name="close" [strokeWidth]="1.5" />
      </button>
    }
  `,
})
export class FeedbackBanner {
  readonly type = input<FeedbackBannerType>('info');

  readonly heading = input<string>();

  readonly description = input<string>();

  /** Overrides the type's default glyph. */
  readonly icon = input<IconInput>();

  readonly dismissible = input(false, { transform: booleanAttribute });

  readonly dismissLabel = input('Dismiss');

  /**
   * Whether the banner is a live region. On by default, because a banner almost
   * always appears in response to something. Turn it off for one that is part of
   * the page from the start — a live region that fires on load is noise.
   */
  readonly announce = input(true, { transform: booleanAttribute });

  readonly dismiss = output<void>();

  protected readonly TYPE_ICONS = TYPE_ICONS;

  /** An error interrupts; the rest wait their turn. */
  protected readonly role = computed(() => {
    if (!this.announce()) return null;
    return this.type() === 'error' ? 'alert' : 'status';
  });

  protected readonly hostClasses = computed(
    () => `ui-feedback-banner ui-feedback-banner--${this.type()}`,
  );
}

/**
 * CTA Banner — Figma's `CTA Banner` (node 73:18530),
 * `Direction`(Horizontal/Vertical) × `State`(Default/Hover).
 *
 * ```html
 * <ui-cta-banner heading="Medium length headline" description="Design better…">
 *   <ui-badge uiCtaBannerBadge size="md">New</ui-badge>
 *   <img uiCtaBannerMedia src="/promo.svg" alt="" />
 *   <span uiCtaBannerActions>
 *     <button ui-button size="lg">Get started</button>
 *     <button ui-button variant="secondary" size="lg">Learn more</button>
 *   </span>
 * </ui-cta-banner>
 * ```
 *
 * `Horizontal` sets the media beside the copy, `Vertical` beneath it; both halves
 * share the width equally in the horizontal one, which is Figma's `flex: 1 0 0` on
 * each.
 *
 * **The media is a slot, not a drawing.** Figma fills it with a 16-tile geometric
 * pattern in one variant and a 60-shape pebble illustration in another, neither of
 * which is a published component or an exported asset — so the app supplies its
 * own. See feedback #94.
 */
@Component({
  selector: 'ui-cta-banner',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './cta-banner.scss',
  host: { '[class]': 'hostClasses()' },
  template: `
    <div class="ui-cta-banner__container">
      <ng-content select="[uiCtaBannerBadge]" />

      <div class="ui-cta-banner__text">
        <p class="ui-cta-banner__heading">{{ heading() }}</p>
        @if (description()) {
          <p class="ui-cta-banner__description">{{ description() }}</p>
        }
      </div>

      <ng-content select="[uiCtaBannerActions]" />
    </div>

    <div class="ui-cta-banner__media"><ng-content select="[uiCtaBannerMedia]" /></div>
  `,
})
export class CtaBanner {
  readonly heading = input.required<string>();

  readonly description = input<string>();

  readonly direction = input<CtaBannerDirection>('horizontal');

  protected readonly hostClasses = computed(
    () => `ui-cta-banner ui-cta-banner--${this.direction()}`,
  );
}

/*
 * The three slot markers below carry inline host styles rather than classes: each
 * sits on an element the CONSUMER owns, which keeps the consumer's style
 * encapsulation, so no rule scoped to this component could reach it.
 */

/** Pins the badge to the start of the column instead of letting it stretch. */
@Directive({
  selector: '[uiCtaBannerBadge]',
  host: { class: 'ui-cta-banner__badge', style: 'align-self:flex-start' },
})
export class CtaBannerBadge {}

/** Figma's `Additional` row: the buttons, 8px apart. */
@Directive({
  selector: '[uiCtaBannerActions]',
  host: {
    class: 'ui-cta-banner__actions',
    style: 'display:flex; flex-wrap:wrap; gap:var(--space-200)',
  },
})
export class CtaBannerActions {}

/** Fills the media half, whatever the app puts there. */
@Directive({
  selector: '[uiCtaBannerMedia]',
  host: {
    class: 'ui-cta-banner__media-content',
    style: 'display:block; inline-size:100%; block-size:100%; object-fit:cover',
  },
})
export class CtaBannerMedia {}

/** Keeps the feedback banner's action button from being squeezed by long text. */
@Directive({
  selector: '[uiFeedbackBannerAction]',
  host: { class: 'ui-feedback-banner__action', style: 'flex:none; align-self:center' },
})
export class FeedbackBannerAction {}
